import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EMAIL_LINK_TYPES, type EmailLinkProof, type EmailLinkType } from '@libs/dtos/auth/auth.dto';
import { AuthService } from '@services/auth/auth.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { AuthFormComponent } from '@shared/components/auth-form/auth-form.component';

@Component({
	selector: 'app-reset-password-confirm',
	imports: [AuthFormComponent],
	templateUrl: './reset-password-confirm.component.html',
	styleUrl: './reset-password-confirm.component.scss',
})
export class ResetPasswordConfirmComponent implements OnInit {
	success = false;
	loading = false;
	/** What proves the link: its token_hash, or the session of an older link. */
	link: EmailLinkProof | null = null;

	constructor(
		private messageAlertService: MessageAlertService,
		private route: ActivatedRoute,
		private router: Router,
		private authService: AuthService,
		private translocoService: TranslocoService,
	) {}

	ngOnInit(): void {
		// Current emails carry `?token_hash=…&type=recovery` (24/09), spent by the
		// backend when the form is sent — NOT on opening, so a mail scanner
		// visiting the link cannot burn it. Older links bring the session in the
		// fragment (Supabase's `/verify` redirect) and keep working.
		const query = this.route.snapshot.queryParamMap;
		const tokenHash = query.get('token_hash');
		const type = query.get('type') as EmailLinkType | null;
		if (tokenHash && type && EMAIL_LINK_TYPES.includes(type)) {
			this.link = { tokenHash, type };
			return;
		}

		this.route.fragment.subscribe((fragment) => {
			if (fragment) {
				const params = new URLSearchParams(fragment);
				const accessToken = params.get('access_token');
				this.link = accessToken ? { accessToken } : null;

				if (!accessToken) {
					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						this.translocoService.translate('auth.resetPasswordConfirm.messages.invalidLink'),
					);
					setTimeout(() => {
						this.router.navigate(['/reset-password']);
					}, 3000);
				}
			}
		});
	}

	onSubmit(formData: { newPassword?: string; confirmPassword?: string }): void {
		// The template now feeds [loading] to the form (button disabled), and
		// this guard covers the submit that was already in flight.
		if (this.loading) {
			return;
		}
		if (!this.link) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('auth.resetPasswordConfirm.messages.invalidLink'),
			);
			return;
		}

		this.loading = true;
		const { newPassword } = formData;

		if (!newPassword) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('auth.resetPasswordConfirm.messages.passwordRequired'),
			);
			this.loading = false;
			return;
		}

		this.authService.updatePasswordWithToken(this.link, newPassword).subscribe({
			next: () => {
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('common.labels.success'),
					this.translocoService.translate('auth.resetPasswordConfirm.messages.passwordUpdated'),
				);
				this.success = true;
				this.loading = false;
			},
			error: () => {
				this.loading = false;
				// Error is already handled by the service
			},
		});
	}
}
